import type { Call } from '@entities/call';
import { CallStatuses } from '@entities/call';
import { CallTypes } from '@entities/call';
import type { BaseUsecase } from '@usecases/base-usecase';
import type { SearchRecordResult } from '@main/sf-connector/scv-object-manager';
import { ScvObjectManager } from '@main/sf-connector/scv-object-manager';

export type DialNumberRequest = {
  callId: string;
  to: string;
};

export interface DialNumberRepository {
  insertOutboundCall(call: Call): Promise<void>;
}

export interface DialNumberUserRepository {
  getOutboundNumber(): Promise<string>;
}

export interface DialNumberEventPublisher {
  publish(call: Call): Promise<void>;
}

export interface DialNumberUsecase
  extends BaseUsecase<DialNumberRequest, void> {
  run(call: DialNumberRequest): Promise<void>;
}

export class DialNumber implements DialNumberUsecase {
  constructor(
    private userRepository: DialNumberUserRepository,
    private repository: DialNumberRepository,
    private publisher: DialNumberEventPublisher,
  ) {}

  async run({ callId: id, to }: DialNumberRequest): Promise<void> {
    const usecaseStartTime = new Date().toISOString();
    console.log('dial-number usecase started', {
      callId: id,
      to,
      timestamp: usecaseStartTime,
    });

    const getNumberStartTime = new Date().toISOString();
    const from = await this.userRepository.getOutboundNumber();
    console.log('outbound number retrieved', {
      callId: id,
      from,
      timestamp: new Date().toISOString(),
      duration: Date.now() - new Date(getNumberStartTime).getTime(),
    });

    const call: Call = {
      id,
      to,
      from,
      type: CallTypes.OUTBOUND,
      status: CallStatuses.DIALING,
      isMuted: false,
    };

    const searchRecordStartTime = new Date().toISOString();
    const searchRecordResult: SearchRecordResult | undefined =
      await ScvObjectManager.searchRecord(to, true, false);
    console.log('searchRecord completed', {
      callId: id,
      recordFound: !!searchRecordResult,
      timestamp: new Date().toISOString(),
      duration: Date.now() - new Date(searchRecordStartTime).getTime(),
    });

    const recordToLink: SearchRecordResult | undefined = searchRecordResult;
    if (recordToLink) {
      setTimeout(async () => {
        this.linkRecordWithVoiceObject(id, recordToLink);
      }, 3000);
    }

    const publishStartTime = new Date().toISOString();
    await Promise.all([
      this.publisher.publish(call),
      this.repository.insertOutboundCall(call),
    ]);
    console.log('publish and insertOutboundCall completed', {
      callId: id,
      timestamp: new Date().toISOString(),
      duration: Date.now() - new Date(publishStartTime).getTime(),
      totalUsecaseDuration: Date.now() - new Date(usecaseStartTime).getTime(),
    });
  }

  private async linkRecordWithVoiceObject(
    vendorCallKey: string,
    recordToLink?: SearchRecordResult,
  ): Promise<void> {
    const [voiceObject] = await Promise.all([
      ScvObjectManager.searchVoiceObject(vendorCallKey, ''),
    ]);

    if (voiceObject) {
      await ScvObjectManager.updateVoiceObject(
        vendorCallKey,
        vendorCallKey,
        recordToLink ? recordToLink.Id : undefined,
      );
    }
  }
}
